import { useEffect, useState, type ReactNode } from 'react';

import { ApiError } from '../api/client.js';
import type { ArchivedContent, Item } from '../api/types.js';

/**
 * The archived article, in a frame of its own (M3.11.1).
 *
 * ## Why an `<iframe>` and not `dangerouslySetInnerHTML`
 *
 * Feed summaries are inlined, because they were sanitised by us at ingest and
 * the allow-list is ours. An archived article is a different animal: it is a
 * whole page from a publisher we do not control, and although it is sanitised
 * too, the consequence of a single miss is different. Inlined, a surviving
 * script runs with the app's origin — its cookies, its storage, its access to
 * the API as the signed-in reader. In a sandboxed frame on a *separate*
 * origin (M3.8.2) the same script can reach nothing at all.
 *
 * So the defence is layered and each layer assumes the one above it failed:
 * the sanitiser, then the separate origin, then `sandbox`, then the content
 * origin's own `default-src 'none'` CSP.
 *
 * `sandbox` carries **no** `allow-scripts` and **no** `allow-same-origin`.
 * Together those two are the pair that matters: granting both is equivalent
 * to no sandbox at all, because the framed page can then remove its own
 * sandbox attribute. An archived article is a document to read; it needs
 * neither.
 *
 * ## Every state is designed, including the boring ones
 *
 * M3.11.1's fourth criterion. An archive that silently shows nothing while it
 * works, or that says "error" when the publisher simply disallowed us, trains
 * a reader to stop trying it — and this is the feature people are being asked
 * to pay for.
 */

export type ArchiveState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly content: ArchivedContent }
  /** Extraction has not finished. Worth waiting for, so say so. */
  | { readonly kind: 'pending' }
  /** Nothing archivable, or the publisher declined. Not an error. */
  | { readonly kind: 'original-only' }
  | { readonly kind: 'taken-down' }
  | { readonly kind: 'failed'; readonly message: string };

export interface ArchivedArticleProps {
  readonly item: Item;
  readonly fetchContent: (itemId: string) => Promise<ArchivedContent>;
  /** Reports the open, with the item's age (M3.11.2). */
  readonly onOpened?: (ageDays: number) => void;
  readonly now?: () => Date;
}

export function ArchivedArticle(props: ArchivedArticleProps): ReactNode {
  const [state, setState] = useState<ArchiveState>({ kind: 'idle' });

  const { item, fetchContent } = props;

  useEffect(() => {
    let cancelled = false;

    setState({ kind: 'loading' });

    fetchContent(item.id)
      .then((content) => {
        if (!cancelled) {
          setState({ kind: 'ready', content });
        }
      })
      .catch((error: unknown) => {
        if (cancelled) {
          return;
        }
        setState(classify(error));
      });

    /*
     * Guards against a reader moving on before the request lands. Without it
     * the previous item's article would appear under the new item's heading,
     * which reads as the app showing the wrong article rather than as a race.
     */
    return () => {
      cancelled = true;
    };
  }, [item.id, fetchContent]);

  /*
   * The open is reported once the frame actually has something, not when the
   * request starts — an open that failed is not an open, and counting it
   * would inflate the one metric the PRD leans on.
   */
  useEffect(() => {
    if (state.kind !== 'ready' || props.onOpened === undefined) {
      return;
    }
    props.onOpened(ageInDays(item.publishedAt, props.now?.() ?? new Date()));
  }, [state.kind, item.publishedAt, props]);

  switch (state.kind) {
    case 'idle':
    case 'loading':
      return (
        <div className="archive archive--loading" role="status">
          <p className="state__text">Loading the archived article…</p>
        </div>
      );

    case 'pending':
      return (
        <div className="archive archive--pending" role="status">
          <p className="state__text">We are still fetching this article.</p>
          <p className="state__meta">
            It usually takes a minute or two. The summary is below in the meantime.
          </p>
        </div>
      );

    case 'original-only':
      return (
        <div className="archive archive--none">
          <p className="state__text">There is no archived copy of this one.</p>
          <p className="state__meta">
            The publisher asked us not to keep one, or we could not get a clean copy of
            the page. The original is still a click away.
          </p>
        </div>
      );

    case 'taken-down':
      return (
        <div className="archive archive--gone">
          <p className="state__text">This archived article is no longer available.</p>
          <p className="state__meta">It was removed at the publisher’s request.</p>
        </div>
      );

    case 'failed':
      return (
        <div className="archive archive--failed" role="alert">
          <p className="state__text">The archived article could not be loaded.</p>
          <p className="state__meta">{state.message}</p>
        </div>
      );

    case 'ready':
      return (
        <iframe
          className="archive__frame"
          title={state.content.title ?? item.title}
          src={state.content.url}
          /*
           * No `allow-scripts` and no `allow-same-origin`. Granting both is
           * equivalent to removing the sandbox, because the framed document
           * can then reach up and delete the attribute itself.
           */
          sandbox=""
          // The publisher should not learn which of our readers opened them.
          referrerPolicy="no-referrer"
          loading="lazy"
        />
      );
  }
}

/** Turns the gate's status codes into the states above (M3.8.1). */
export function classify(error: unknown): ArchiveState {
  if (!(error instanceof ApiError)) {
    return {
      kind: 'failed',
      message: error instanceof Error ? error.message : 'Something went wrong.',
    };
  }

  switch (error.status) {
    case 409:
      return { kind: 'pending' };
    case 410:
      return { kind: 'taken-down' };
    /*
     * A reader with no grant, and an item that was never archived, are the
     * same thing to a person: there is nothing to read here. Showing "not
     * found" would suggest something is broken.
     */
    case 404:
      return { kind: 'original-only' };
    default:
      return { kind: 'failed', message: 'Please try again in a moment.' };
  }
}

/**
 * The item's age when it was opened, in whole days (M3.11.2).
 *
 * The age is the measurement, not the open. H3 asks whether people read
 * things *older than thirty days* — a count of opens says only that the
 * feature was clicked.
 */
export function ageInDays(publishedAt: string, at: Date): number {
  const published = new Date(publishedAt).getTime();

  if (Number.isNaN(published)) {
    return 0;
  }

  return Math.max(0, Math.floor((at.getTime() - published) / 86_400_000));
}
